/**
 * E2E Mock Data Interceptor
 *
 * Provides mock responses for critical bootstrap endpoints during E2E testing.
 * This prevents tests from hitting the real backend with invalid mock tokens.
 */

import { apiLogger } from '@/core/utils/logger';

/**
 * Check if we're in E2E mock auth mode.
 * This is determined by the presence of the e2e_mock_auth cookie set by /api/e2e/login.
 */
export function isE2EMockAuthMode(): boolean {
  if (typeof window === 'undefined') return false;
  return document.cookie.includes('e2e_mock_auth=1');
}

/**
 * Get mock user data from sessionStorage (set by E2E auth-helpers).
 */
function getE2EMockUser(): Record<string, unknown> | null {
  if (typeof window === 'undefined') return null;
  try {
    const mockUserStr = sessionStorage.getItem('e2e_mock_user');
    if (mockUserStr) {
      return JSON.parse(mockUserStr);
    }
  } catch {
    // Ignore parse errors
  }
  return null;
}

/**
 * Default mock user data when sessionStorage isn't available.
 * This ensures E2E tests can proceed even if the init script timing is off.
 */
const DEFAULT_E2E_MOCK_USER = {
  id: 'e2e-user-default',
  email: 'e2e-test@voley.test',
  firstName: 'E2E',
  lastName: 'Test User',
  organizationId: 'e2e-org-default',
  stytchUserId: 'e2e-stytch-user-default',
};

/**
 * Generate mock response for E2E testing.
 * Returns mock data for critical bootstrap endpoints to avoid hitting the real backend.
 */
export function getE2EMockResponse(
  url: string,
  _method: string
): { data: unknown } | null {
  if (!isE2EMockAuthMode()) return null;

  // Try to get mock user from sessionStorage, fall back to default
  const mockUser = getE2EMockUser() || DEFAULT_E2E_MOCK_USER;

  // Mock /users/me/profile endpoint
  if (url.includes('/users/me/profile')) {
    apiLogger.info('[E2E Mock] Returning mock response for /users/me/profile');
    return {
      data: {
        success: true,
        data: {
          id: mockUser.id || 'e2e-user-default',
          email: mockUser.email || 'e2e-test@voley.test',
          firstName: mockUser.firstName || 'E2E',
          lastName: mockUser.lastName || 'Test User',
          organizationId: mockUser.organizationId || 'e2e-org-default',
          stytchUserId: mockUser.stytchUserId || 'e2e-stytch-user-default',
          profileImageUrl: null,
          phoneNumber: null,
          role: 'owner',
          status: 'active',
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        },
      },
    };
  }

  // Mock /organizations/:id endpoint
  // Note: RTK Query URLs may omit the leading slash (e.g. "organizations/id")
  if (url.match(/\/?organizations\/[^/]+$/)) {
    const orgId = mockUser.organizationId || 'e2e-org-default';
    apiLogger.info(`[E2E Mock] Returning mock response for /organizations/${orgId}`);
    return {
      data: {
        success: true,
        data: {
          id: orgId,
          name: 'E2E Test Organization',
          timezone: 'America/New_York',
          businessHours: {
            monday: { start: '09:00', end: '17:00' },
            tuesday: { start: '09:00', end: '17:00' },
            wednesday: { start: '09:00', end: '17:00' },
            thursday: { start: '09:00', end: '17:00' },
            friday: { start: '09:00', end: '17:00' },
          },
          settings: {},
          subscription_status: 'active',
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        },
      },
    };
  }

  // Mock /auth/validate endpoint
  if (url.includes('/auth/validate')) {
    apiLogger.info('[E2E Mock] Returning mock response for /auth/validate');
    return {
      data: {
        success: true,
        data: {
          valid: true,
          user: mockUser,
        },
      },
    };
  }

  // Mock /conversations endpoint (list)
  if (url.match(/\/?conversations(\?|$)/) && !url.includes('/conversations/')) {
    apiLogger.info('[E2E Mock] Returning mock response for /conversations');
    return {
      data: {
        success: true,
        data: [
          {
            id: 'e2e-conversation-1',
            contact_id: 'e2e-contact-1',
            organization_id: mockUser.organizationId || 'e2e-org-default',
            status: 'active',
            unread_count: 0,
            last_message: {
              id: 'e2e-message-1',
              content: 'Hello from E2E test!',
              direction: 'inbound',
              created_at: new Date().toISOString(),
            },
            contact: {
              id: 'e2e-contact-1',
              first_name: 'E2E',
              last_name: 'Contact',
              phone_number: '+15551234567',
            },
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
          },
        ],
        pagination: {
          total: 1,
          page: 1,
          limit: 20,
          totalPages: 1,
        },
      },
    };
  }

  // Mock single conversation endpoint
  if (url.match(/\/?conversations\/[^/]+$/)) {
    apiLogger.info('[E2E Mock] Returning mock response for single conversation');
    return {
      data: {
        success: true,
        data: {
          id: 'e2e-conversation-1',
          contact_id: 'e2e-contact-1',
          organization_id: mockUser.organizationId || 'e2e-org-default',
          status: 'active',
          unread_count: 0,
          contact: {
            id: 'e2e-contact-1',
            first_name: 'E2E',
            last_name: 'Contact',
            phone_number: '+15551234567',
          },
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        },
      },
    };
  }

  // Mock messages endpoint
  if (url.match(/\/?conversations\/[^/]+\/messages/)) {
    apiLogger.info('[E2E Mock] Returning mock response for messages');
    return {
      data: {
        success: true,
        data: [
          {
            id: 'e2e-message-1',
            conversation_id: 'e2e-conversation-1',
            content: 'Hello from E2E test!',
            direction: 'inbound',
            status: 'delivered',
            created_at: new Date().toISOString(),
          },
        ],
        pagination: {
          total: 1,
          page: 1,
          limit: 50,
          totalPages: 1,
        },
      },
    };
  }

  return null;
}
